import React, {useEffect} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  TextField
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

export type Option = {
  label: string;
  value: string;
};

type Props = {
  options: Option[];
  multiple?: boolean;   
  value: string | string[];   

  onChange: (value: string | string[]) => void;

  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  fullwidth?: boolean;

  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onBlur?: () => void;

  onScrollEnd?: () => void;
  error?: boolean;
  sx?: SxProps<Theme>;
};

const AutoCompleteSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  multiple = false,
  placeholder,
  loading,
  searchable,
  searchValue,
  onSearchChange,
  onScrollEnd,
  onBlur,
  fullwidth = false,
  disabled = false,
  error = false,
  sx,
}) => {
 
  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const scrollTopRef = React.useRef(0);

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState(searchValue || "");

  const [selectedCache, setSelectedCache] = React.useState<Option[]>([]);
  const [internalOptions, setInternalOptions] = React.useState<Option[]>([]);

  const isFetchingMoreRef = React.useRef(false);

  React.useEffect(() => {
    if (searchValue !== undefined) {
      setSearch(searchValue);
    }
  }, [searchValue]);

  React.useEffect(() => {
    //const newSelected = options.filter((o) => value.includes(o.value));
    const newSelected = options.filter((o) =>
      Array.isArray(value)
        ? value.includes(o.value)
        : value === o.value
    );

    setSelectedCache((prev) => {
      const merged = [...prev];

      newSelected.forEach((opt) => {
        if (!merged.find((p) => p.value === opt.value)) {
          merged.push(opt);
        }
      });

      return merged;
    });
  }, [options, value]);

  // Notify parent of search changes — but skip the initial mount so we don't
  // emit a spurious empty search that can wipe parent-managed option state
  // (e.g. when this component mounts after the options query has resolved).
  const didMountSearchRef = React.useRef(false);
  useEffect(() => {
    if (!didMountSearchRef.current) {
      didMountSearchRef.current = true;
      return;
    }
    onSearchChange?.(search);
  }, [search]);

  React.useEffect(() => {
    scrollTopRef.current = 0;
  }, [search]);

  /*const selectedOptions = React.useMemo(() => { 
    return selectedCache.filter((opt) => value.includes(opt.value)); 
  }, [selectedCache, value]);*/

  const selectedOptions = React.useMemo(() => {
    if (Array.isArray(value)) {
      return selectedCache.filter((opt) => value.includes(opt.value));
    } else {
      return selectedCache.find((opt) => opt.value === value) || null;
    }
  }, [selectedCache, value]);

  React.useEffect(() => {
    // When a searchValue prop is provided (externally controlled search), replace
    // internalOptions entirely so new search results are always reflected.
    // When there is no searchValue (uncontrolled / pagination-only), append so
    // scroll-based pagination keeps accumulating pages.
    if (searchValue !== undefined) {
      setInternalOptions(options);
    } else {
      setInternalOptions((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const newItems = options.filter((opt) => !existing.has(opt.value));
        return [...prev, ...newItems];
      });
    }
  }, [options]);

  const mergedOptions = React.useMemo(() => {
    const sourceOptions = internalOptions.length ? internalOptions : options;

    return [
      ...selectedCache,
      ...sourceOptions.filter(
        (o) => !selectedCache.some((s) => s.value === o.value)
      ),
    ];
  }, [internalOptions, selectedCache, options]);


  useEffect(() => {
    if (searchValue !== undefined) {
      setInternalOptions([]);
    }

    setSelectedCache((prev) =>
      prev.filter((opt) =>
        Array.isArray(value)
          ? value.includes(opt.value)
          : value === opt.value
      )
    );

    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchValue, value]);

  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollTopRef.current;

      // ✅ allow next fetch
      isFetchingMoreRef.current = false;
    }
  }, [mergedOptions]);

  const Listbox = React.forwardRef<HTMLUListElement, any>(
    function Listbox(props, ref) {
      const { children, ...rest } = props;

      return (
        <ul
          {...rest}
          ref={(node) => {
            listRef.current = node;

            if (typeof ref === "function") ref(node);
            else if (ref) (ref as any).current = node;
          }}
          style={{ maxHeight: 250, overflow: "auto" }}
          onScroll={(e) => {
            const list = e.currentTarget;

            // ✅ save scroll position
            scrollTopRef.current = list.scrollTop;

            if (
              !isFetchingMoreRef.current &&
              !loading &&
              onScrollEnd &&
              list.scrollTop + list.clientHeight >= list.scrollHeight - 20
            ) {
              isFetchingMoreRef.current = true;

              // ✅ preserve exact position before fetch
              scrollTopRef.current = list.scrollTop;

              onScrollEnd();
            }
          }}
        >
          {children}
        </ul>
      );
    }
  );

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); onBlur?.(); }}>
      <div>
        <Autocomplete
          fullWidth
          multiple={multiple}
          disableCloseOnSelect={multiple}   // ✅ only for multi-select
          autoHighlight={false}
          autoSelect={false}
          handleHomeEndKeys={false}
          disableListWrap
          open={open}
          onOpen={() => {
            if (!disabled) setOpen(true);
          }}
          onClose={(_, reason) => {
            if (reason === "blur") return;
            setOpen(false);
          }}
          options={mergedOptions.length ? mergedOptions : options}
          value={selectedOptions}
          loading={loading}

          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}

          filterOptions={(x) => x}

          onChange={(_, newValue) => {
            if (multiple) {
              onChange((newValue as Option[]).map((v) => v.value));
            } else {
              onChange((newValue as Option | null)?.value || "");
              setOpen(false); // ✅ close dropdown after select
            }
          }}

          PaperComponent={(props) => (
            <Paper 
              { ...props}
                sx={{
                  width: fullwidth ? "100%" : 260,
                  minWidth: fullwidth ? "100%" : 260,
                  maxWidth: fullwidth ? "100%" : 360,
                  "& .MuiAutocomplete-listbox": {
                    paddingTop: 0,
                  },
                }}
            >
              {searchable && (
                <Box sx={{ px: 1, pt: 1, pb: 0 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={search}
                    autoFocus={!disabled}
                    disabled={disabled}
                    onChange={(e) => {
                      scrollTopRef.current = 0;   // ✅ ADD HERE
                      setSearch(e.target.value);
                      setInternalOptions([]);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      endAdornment: search && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearch("");
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                </Box>
              )}
              {props.children}
            </Paper>
          )}

          renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props;

          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                py: 0.1,
                px: 1,
                minHeight: "10px",
                fontSize: "15px",
                whiteSpace: "nowrap",
                width: "max-content",
                minWidth: "100%"
              }}
            >
              {multiple && (
                <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} disabled={disabled} />
              )}
              {option.label}
            </Box>
          );
        }}

          ListboxComponent={Listbox}

          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
              />
              ))
          }

          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={error}
              placeholder={placeholder}
              sx={[
                {
                  "& .MuiInputBase-root": {
                    minHeight: "40px",
                    height: "auto",
                    backgroundColor: "#EFF3F6",
                    ...(multiple && {
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      maxHeight: "72px",
                      overflowY: "auto",
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    }),
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 12px",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    marginTop: 0,
                  },
                  mb: 0,
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
              ]}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
              disabled={disabled}
            />
          )}
          disabled={disabled}
        />
      </div>
    </ClickAwayListener>
  );
};

export default AutoCompleteSelect;